// The documents the form asks for: the type names the stored file, the
// subfolder says where the claim files it.

import type {
  IntakeLanguage,
  IntakeRequirement,
  IntakeSubfolder,
  IntakeTemplateReserved,
} from "@duralex/shared";
import {
  REQUIREMENTS,
  REQUIREMENT_NAMES,
  SUBFOLDERS,
  move,
  newChecklistItem,
  toggleCode,
  without,
  type Draft,
  rowKey,
  followLabel,
} from "./draft";
import { CodeChips, IdField, LabelField, RowActions } from "./fields";

export function ChecklistEditor({
  draft,
  lang,
  reserved,
  existing,
  stale,
  ai,
  onChange,
}: {
  draft: Draft;
  lang: IntakeLanguage;
  reserved: IntakeTemplateReserved;
  existing: Set<string>;
  /** The keys whose `lang` translation is out of date. */
  stale: Set<string>;
  /** The keys whose `lang` cell is a model's unsaved proposal. */
  ai: Set<string>;
  onChange: (draft: Draft) => void;
}) {
  const set = (checklist: Draft["checklist"]) => onChange({ ...draft, checklist });
  const patch = (i: number, p: Partial<Draft["checklist"][number]>) =>
    set(draft.checklist.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const ids = draft.checklist.map((d) => d.id);
  return (
    <div className="ie-list">
      {draft.checklist.map((d, i) => {
        const isReserved = reserved.checklist.includes(d.id);
        const isNew = !existing.has(`doc:${d.id}`);
        return (
          <div key={rowKey(ids, i)} className="ie-doc">
            <div className="ie-q-head">
              <IdField
                label={`Document id (${d.id})`}
                value={d.id}
                locked={isReserved || !isNew}
                onChange={(id) => patch(i, { id })}
              />
              <select
                className="cc-field ie-select"
                aria-label={`Requirement of ${d.id}`}
                value={d.required}
                onChange={(e) => patch(i, { required: e.target.value as IntakeRequirement })}
              >
                {REQUIREMENTS.map((r) => (
                  <option key={r} value={r}>
                    {REQUIREMENT_NAMES[r]}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="cc-field ie-id"
                aria-label={`File type of ${d.id}`}
                title="Names the stored file: <Type>_<Last-First>_<date>"
                value={d.docType}
                onChange={(e) => patch(i, { docType: e.target.value })}
              />
              <select
                className="cc-field ie-select"
                aria-label={`Folder of ${d.id}`}
                value={d.subfolder}
                onChange={(e) => patch(i, { subfolder: e.target.value as IntakeSubfolder })}
              >
                {SUBFOLDERS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
              <RowActions
                what={`document ${d.id}`}
                onUp={() => set(move(draft.checklist, i, -1))}
                onDown={() => set(move(draft.checklist, i, 1))}
                onRemove={() => set(without(draft.checklist, i))}
                locked={isReserved ? "The firm always asks for a photo ID" : undefined}
              />
            </div>
            <LabelField
              label={`Document ${d.id}`}
              text={d.label}
              lang={lang}
              stale={stale.has(`doc.${d.id}`)}
              ai={ai.has(`doc.${d.id}`)}
              onChange={(label) =>
                patch(i, {
                  label,
                  id: isNew ? followLabel(d.id, d.label.en, label.en, "document") : d.id,
                })
              }
            />
            <CodeChips
              label={`Asked for (${d.id})`}
              value={d.appliesTo}
              categories={draft.categories}
              onToggle={(code) => patch(i, { appliesTo: toggleCode(d.appliesTo, code) })}
            />
          </div>
        );
      })}
      <button
        type="button"
        className="cc-link-btn"
        onClick={() =>
          set([...draft.checklist, newChecklistItem(new Set(draft.checklist.map((d) => d.id)))])
        }
      >
        Add a document
      </button>
    </div>
  );
}
